import React, { useRef, useState, useCallback } from "react";
import Papa from "papaparse";
import { Nav, NavItem, NavLink, TabContent } from "reactstrap";
import classnames from "classnames";

import { useProfile } from "Components/Hooks/UserHooks";
import { importContractor } from "common/services/contractors.service";

import {
  FieldMapping,
  ImportResults,
  ImportStatus,
  MAX_IMPORT_RECORDS,
  autoMapFields,
  mapRowToRecord,
} from "./contractorImportFields";
import ContractorsImportWizardTabFileUpload from "./ContractorsImportWizardTabFileUpload";
import ContractorsImportWizardTabFieldMapping from "./ContractorsImportWizardTabFieldMapping";
import ContractorsImportWizardTabDataPreview from "./ContractorsImportWizardTabDataPreview";
import ContractorsImportWizardTabFinish from "./ContractorsImportWizardTabFinish";

/** 5-minute fetch timeout for the bulk import request. */
const IMPORT_TIMEOUT_MS = 5 * 60 * 1000;

const ContractorsImportWizard: React.FC = () => {
  const { token } = useProfile();

  // ── wizard navigation ──────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(1);
  const [, setPassedSteps] = useState<number[]>([1]);

  function toggleTab(tab: number) {
    if (activeTab === tab) return;
    if (tab < 1 || tab > 4) return;
    // Prevent jumping forward past tab 1 without a valid file
    if (tab > 1 && !fileReady) return;
    setActiveTab(tab);
    setPassedSteps((prev) => [...prev, tab]);
  }

  // ── file-upload state ──────────────────────────────────────────────────────
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [firstLineIncludesFieldNames, setFirstLineIncludesFieldNames] =
    useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── parsed CSV data ────────────────────────────────────────────────────────
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<Record<string, string>[]>([]);
  const [csvParseError, setCsvParseError] = useState<string | null>(null);

  // A file must be successfully parsed before the user can advance past tab 1.
  const fileReady = !!selectedFile && csvRows.length > 0 && !csvParseError;

  // ── field mapping ──────────────────────────────────────────────────────────
  const [fieldMappings, setFieldMappings] = useState<FieldMapping>({});

  // ── import state ───────────────────────────────────────────────────────────
  const [importStatus, setImportStatus] = useState<ImportStatus>("idle");
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [importResults, setImportResults] = useState<ImportResults | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── helpers ────────────────────────────────────────────────────────────────
  const formatFileSize = (bytes: number) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return "0 KB";
    const kb = bytes / 1024;
    return kb < 1024 ? `${kb.toFixed(2)} KB` : `${(kb / 1024).toFixed(2)} MB`;
  };

  const openFilePicker = () => fileInputRef.current?.click();

  // ── file selection + CSV parsing ───────────────────────────────────────────
  const handlePickedFiles = (files: FileList | File[]) => {
    const file = Array.isArray(files) ? files[0] : files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setCsvParseError(null);
    setCsvHeaders([]);
    setCsvRows([]);
    setFieldMappings({});

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length && results.data.length === 0) {
          setCsvParseError("Could not parse file. Please check the format.");
          return;
        }
        const headers = results.meta.fields ?? [];
        setCsvHeaders(headers);
        setCsvRows(results.data);
        setFieldMappings(autoMapFields(headers));
      },
      error: (err) => {
        setCsvParseError(err.message);
      },
    });
  };

  // ── start over ─────────────────────────────────────────────────────────────
  const handleStartOver = () => {
    stopProgressSimulation();
    setSelectedFile(null);
    setIsDragActive(false);
    setFirstLineIncludesFieldNames(true);
    setCsvHeaders([]);
    setCsvRows([]);
    setCsvParseError(null);
    setFieldMappings({});
    setImportStatus("idle");
    setImportProgress({ current: 0, total: 0 });
    setImportResults(null);
    setImportErrors([]);
    setActiveTab(1);
    setPassedSteps([1]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ── import ─────────────────────────────────────────────────────────────────
  const stopProgressSimulation = useCallback(() => {
    if (progressIntervalRef.current !== null) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  const runImport = async () => {
    // Cap rows to the maximum supported by a single request
    const rowsToImport = csvRows.slice(0, MAX_IMPORT_RECORDS);
    const total = rowsToImport.length;

    setImportStatus("running");
    setImportErrors([]);
    setImportProgress({ current: 0, total });

    // Simulate progress: eases asymptotically toward 90% while the request
    // is in flight, then jumps to 100% on completion.
    let simPct = 0;
    progressIntervalRef.current = setInterval(() => {
      simPct = simPct + (90 - simPct) * 0.07;
      setImportProgress({
        current: Math.round((simPct / 100) * total),
        total,
      });
    }, 400);

    try {
      const records = rowsToImport.map((row) =>
        mapRowToRecord(row, fieldMappings)
      );

      const result = await importContractor(
        { data: records },
        token,
        { timeoutMs: IMPORT_TIMEOUT_MS }
      );

      const successful: number = result.metadata?.data?.successful ?? 0;
      const failed: number = result.metadata?.data?.failed ?? 0;

      // Use the server-returned blob directly for the failed-records download
      const downloadUrl =
        failed > 0 && result.blob && result.blob.size > 0
          ? URL.createObjectURL(result.blob)
          : null;

      setImportResults({ total, successful, failed, downloadUrl });
    } catch (error: any) {
      const isTimeout = error?.name === "AbortError";
      const msg = isTimeout
        ? "The import request timed out. Try reducing the number of rows or increasing the server timeout."
        : (error?.response?.data?.error?.message ??
          error?.message ??
          "Import request failed.");
      setImportErrors([msg]);
      setImportResults({ total, successful: 0, failed: total, downloadUrl: null });
    } finally {
      stopProgressSimulation();
      // Snap the bar to 100% so it completes visually before switching to done
      setImportProgress({ current: total, total });
      setImportStatus("done");
    }
  };

  const steps = [
    { tab: 1, label: "File Upload",   icon: "ri-upload-cloud-2-line",  locked: false },
    { tab: 2, label: "Field Mapping", icon: "ri-git-merge-line",        locked: !fileReady },
    { tab: 3, label: "Data Preview",  icon: "ri-eye-line",              locked: !fileReady },
    { tab: 4, label: "Import",        icon: "ri-checkbox-circle-line",  locked: !fileReady },
  ];

  return (
    <>
      <div className="step-arrow-nav mb-4">
        <Nav className="nav-pills nav-justified custom-nav" role="tablist">
          {steps.map(({ tab, label, icon, locked }) => (
            <NavItem key={tab} role="presentation">
              <NavLink
                href="#"
                className={classnames(
                  { active: activeTab === tab, done: activeTab > tab },
                  "p-3 fs-15 d-flex align-items-center justify-content-center gap-2",
                  { "text-muted": locked }
                )}
                style={locked ? { pointerEvents: "none", opacity: 0.45 } : undefined}
                title={locked ? "Complete the previous step to unlock" : label}
                onClick={() => toggleTab(tab)}
              >
                <span
                  className={classnames(
                    "d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0",
                    locked
                      ? "bg-light text-muted"
                      : activeTab > tab
                        ? "bg-success-subtle text-success"
                        : "bg-primary-subtle text-primary"
                  )}
                  style={{ width: 30, height: 30, fontSize: 15 }}
                >
                  {activeTab > tab ? (
                    <i className="ri-check-line" />
                  ) : locked ? (
                    <i className="ri-lock-line" />
                  ) : (
                    <i className={icon} />
                  )}
                </span>
                <span className="d-none d-sm-inline">{label}</span>
              </NavLink>
            </NavItem>
          ))}
        </Nav>
      </div>

      <TabContent activeTab={activeTab} className="pt-3">
        <ContractorsImportWizardTabFileUpload
          activeTab={activeTab}
          toggleTab={toggleTab}
          selectedFile={selectedFile}
          isDragActive={isDragActive}
          setIsDragActive={setIsDragActive}
          fileInputRef={fileInputRef}
          openFilePicker={openFilePicker}
          handlePickedFiles={handlePickedFiles}
          firstLineIncludesFieldNames={firstLineIncludesFieldNames}
          setFirstLineIncludesFieldNames={setFirstLineIncludesFieldNames}
          formatFileSize={formatFileSize}
          onStartOver={handleStartOver}
          csvParseError={csvParseError}
          csvRowCount={csvRows.length}
        />
        <ContractorsImportWizardTabFieldMapping
          activeTab={activeTab}
          toggleTab={toggleTab}
          csvHeaders={csvHeaders}
          fieldMappings={fieldMappings}
          setFieldMappings={setFieldMappings}
        />
        <ContractorsImportWizardTabDataPreview
          activeTab={activeTab}
          toggleTab={toggleTab}
          csvHeaders={csvHeaders}
          csvRows={csvRows}
          fieldMappings={fieldMappings}
        />
        <ContractorsImportWizardTabFinish
          csvRows={csvRows}
          fieldMappings={fieldMappings}
          importStatus={importStatus}
          importProgress={importProgress}
          importResults={importResults}
          importErrors={importErrors}
          onRunImport={runImport}
          onStartOver={handleStartOver}
        />
      </TabContent>
    </>
  );
};

export default ContractorsImportWizard;
